import { toast } from '@heidi/design-system/toast';
import { useLatestRef } from '@heidi/utils';
import { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type {
  AgentEvent,
  RunTransport,
  TurnLoaderPhrasesAgentEvent,
} from '../../contracts';
import {
  dispatchAgentEvent,
  type AgentEventDispatchContext,
} from './agent-events/dispatch-agent-event';
import {
  useAgentEventStream,
  type AgentEventStreamStatus,
} from './agent-events/use-agent-event-stream';
import { agentHarnessClient } from './client';
import type { AgentComposerProps } from './components/agent-composer/agent-composer';
import type { AgentTimelineProps } from './components/agent-timeline/agent-timeline';
import {
  useMutateResolveApproval,
  useRunStreamLifecycle,
  type RunStreamEventObserver,
  type RunStreamLifecycleResult,
} from './hooks';

const agentRunChatMessages = defineMessages({
  resolveApprovalFailed: {
    defaultMessage: 'Could not resolve the approval',
    id: 'OmSr9iRa2F',
    description: 'Toast after an approval resolution request fails',
  },
});

type ResolveApprovalHandler = NonNullable<AgentTimelineProps['onResolveApproval']>;

type TimelineProps = Pick<
  AgentTimelineProps,
  'items' | 'onResolveApproval' | 'resolvingApproval'
>;

type ComposerProps = Pick<
  AgentComposerProps,
  | 'agentRunId'
  | 'canSubmit'
  | 'inputState'
  | 'hasMessages'
  | 'cancelling'
  | 'turnInFlight'
  | 'transport'
  | 'onTurnSubmitted'
  | 'onCancelTurn'
>;

export interface UseAgentRunChatOptions {
  transport?: RunTransport;
  /**
   * Opt-in per-run agent-event handling. The harness performs NO side
   * effects of its own — no toasts, navigation, run-title writes, or
   * remote tool execution. Only the handlers you pass here fire; every
   * other event type is logged and ignored. Passing this also opens the
   * agent-event SSE channel (see `subscribeToEvents`).
   */
  eventHandlers?: AgentEventDispatchContext;
  /**
   * Force the per-run agent-event SSE channel open/closed. Defaults to
   * opening only when `eventHandlers` is provided and the run is active.
   */
  subscribeToEvents?: boolean;
  /**
   * Optional observer for the canonical run-scoped LLM stream. Consumers use
   * this for side-channel setup that must follow the same stream ordering as
   * the transcript, without coupling those side effects to the timeline reducer.
   */
  onStreamEvent?: RunStreamEventObserver;
  /**
   * Fired when a turn is cancelled before the agent produces any output (a
   * pre-output stop). Surfaces use this to return the typed prompt to the
   * composer. See `useRunStreamLifecycle`.
   */
  onTurnDiscarded?: () => void;
  /**
   * Drop pre-output cancelled turns (user bubble + marker) from the timeline.
   * Opt-in; pair with `onTurnDiscarded`. See `useRunStreamLifecycle`.
   */
  dropEmptyCancelledTurns?: boolean;
  /**
   * Keep the per-run `/stream` open across turn boundaries (follow mode) so it
   * delivers turns started on other devices — cross-device chat sync. Forwarded
   * to `useRunStreamLifecycle`. Default: closed per turn.
   */
  follow?: boolean;
}

export interface AgentRunChatResult extends RunStreamLifecycleResult {
  onResolveApproval: ResolveApprovalHandler;
  resolvingApproval: boolean;
  eventStreamStatus: AgentEventStreamStatus;
  endedReason: 'closed' | 'failed' | null;
  /** Spread onto `<AgentTimeline />`. */
  timelineProps: TimelineProps;
  /** Spread onto `<AgentComposer />`. */
  composerProps: ComposerProps;
}

const NO_EVENT_HANDLERS: AgentEventDispatchContext = {};

/**
 * One-call chat facade over a public agent run. Bundles the run snapshot
 * + live-stream lifecycle, approval resolution, and (opt-in) per-run
 * agent events, and exposes spread-ready props for `AgentTimeline` and
 * `AgentComposer`. Inject a `transport` to point at a non-Scribe surface
 * (e.g. Projects' prompt-shaped turns) without forking the core.
 */
export const useAgentRunChat = (
  agentRunId: string | undefined,
  {
    transport = agentHarnessClient,
    eventHandlers,
    subscribeToEvents,
    onStreamEvent,
    onTurnDiscarded,
    dropEmptyCancelledTurns,
    follow,
  }: UseAgentRunChatOptions = {}
): AgentRunChatResult => {
  const intl = useIntl();
  const lifecycle = useRunStreamLifecycle(agentRunId, transport, {
    onStreamEvent,
    onTurnDiscarded,
    dropEmptyCancelledTurns,
    follow,
  });
  const {
    run,
    items,
    cancelling,
    turnInFlight,
    handleTurnSubmitted,
    handleCancelTurn,
    handleLoaderPhrases,
  } = lifecycle;

  const { mutateAsync: resolveApprovalMutateAsync, isPending: resolvingApproval } =
    useMutateResolveApproval(transport);

  const onResolveApproval = useCallback<ResolveApprovalHandler>(
    async (approvalId, verdict, reason, modifiedInput) => {
      if (!agentRunId) return;
      try {
        const response = await resolveApprovalMutateAsync({
          agentRunId,
          approvalId,
          body: {
            verdict,
            reason,
            modified_input: modifiedInput ?? null,
          },
        });
        if (!response.accepted) {
          throw new Error('Approval was not accepted');
        }
      } catch (error) {
        toast({
          title: intl.formatMessage(agentRunChatMessages.resolveApprovalFailed),
          tone: 'destructive',
        });
        // Re-throw so the caller (renderer-side wrapper → inline-card
        // dispatch) can clear its pending-verdict latch and let the
        // user retry. On success we do NOT clear the latch: the BE
        // returns 200 well before the harness SSE has echoed the
        // resolution, so re-enabling the buttons in that gap would
        // allow a second click to 409 against the already-resolved
        // gate. Keeping the latch set until the SSE refetch unmounts
        // the card is the safe default.
        throw error;
      }
    },
    [agentRunId, intl, resolveApprovalMutateAsync]
  );

  const eventHandlersRef = useLatestRef(eventHandlers ?? NO_EVENT_HANDLERS);
  const handleLoaderPhrasesRef = useLatestRef(handleLoaderPhrases);
  const onAgentEvent = useCallback(
    (event: AgentEvent) => {
      if (event.type === 'turn.loader_phrases') {
        const loaderEvent = event as TurnLoaderPhrasesAgentEvent;
        const turnId = loaderEvent.turn_id;
        const phrases = loaderEvent.payload?.phrases;
        if (turnId && Array.isArray(phrases) && phrases.length > 0) {
          handleLoaderPhrasesRef.current(turnId, phrases);
        }
      }
      dispatchAgentEvent(event, eventHandlersRef.current);
    },
    [eventHandlersRef, handleLoaderPhrasesRef]
  );

  const subscribe = subscribeToEvents ?? !!eventHandlers;
  const eventStream = useAgentEventStream({
    agentRunId,
    enabled: subscribe && !!agentRunId && run?.status === 'running',
    eventsPath: transport.eventsPath,
    streamHeaders: transport.streamHeaders,
    onEvent: onAgentEvent,
  });

  const timelineProps: TimelineProps = {
    items,
    onResolveApproval,
    resolvingApproval,
  };

  const composerProps: ComposerProps = {
    // Safe empty id while the run id is still resolving; canSubmit is false
    // until the snapshot loads, so the composer never submits against it.
    agentRunId: agentRunId ?? '',
    canSubmit: run?.can_submit ?? false,
    inputState: run?.input_state ?? 'ready',
    hasMessages: items.length > 0,
    cancelling,
    // Drive Stop visibility from the machine's derived flag (not the raw
    // snapshot input_state), so the `ended` phase hides Stop the instant a
    // turn finishes instead of lingering for a poll cycle.
    turnInFlight,
    transport,
    onTurnSubmitted: handleTurnSubmitted,
    onCancelTurn: handleCancelTurn,
  };

  return {
    ...lifecycle,
    onResolveApproval,
    resolvingApproval,
    eventStreamStatus: eventStream.status,
    endedReason: eventStream.endedReason,
    timelineProps,
    composerProps,
  };
};
